import { useCallback, useEffect, useMemo } from "react";
import { useHeadsUpContext } from "@/components/heads-up/context/heads-up-context";

export interface UseIncrementalRevealArgs<T> {
  cardId: string;
  items: readonly T[];
  initialCount: number;
  step: number;
}

export interface UseIncrementalRevealResult<T> {
  visibleItems: T[];
  hasMore: boolean;
  showMore: () => void;
  showAll: () => void;
  reset: () => void;
}

export function useIncrementalReveal<TArgs>(
  args: UseIncrementalRevealArgs<TArgs>
): UseIncrementalRevealResult<TArgs> {
  const { cardId, items, initialCount, step } = args;
  const {
    content: {
      visibility: { get: getCardVisibleCount, set: setCardVisibleCount },
    },
  } = useHeadsUpContext();

  const visibleCount = getCardVisibleCount(cardId) ?? initialCount;

  const visibleItems = useMemo(() => {
    return items.slice(0, visibleCount);
  }, [items, visibleCount]);

  const hasMore = visibleCount < items.length;

  const showMore = useCallback(() => {
    setCardVisibleCount(cardId, current => {
      const base = current ?? initialCount;
      return Math.min(items.length, base + step);
    });
  }, [cardId, initialCount, items.length, setCardVisibleCount, step]);

  const showAll = useCallback(() => {
    setCardVisibleCount(cardId, () => items.length);
  }, [cardId, items.length, setCardVisibleCount]);

  const reset = useCallback(() => {
    setCardVisibleCount(cardId, () => initialCount);
  }, [cardId, initialCount, setCardVisibleCount]);

  useEffect(() => {
    setCardVisibleCount(cardId, current => {
      const base = current ?? initialCount;
      return Math.min(items.length, Math.max(initialCount, base));
    });
  }, [cardId, initialCount, items, setCardVisibleCount]);

  return {
    visibleItems,
    hasMore,
    showMore,
    showAll,
    reset,
  };
}
